import * as React from 'react';
import TooltipNoteIcon from '@/components/ui/Icons/TooltipNoteIcon';
import type { Job } from '@/lib/types/customer/restaurant/StoreProfile';
import HeadlessModal from '@/components/shared/HeadlessModal';
import { useTranslations } from 'next-intl';
import JobDetailsCard from './JobDetailsCard';
import { City } from '@/lib/types/customer/RestaurantJobs';
import { htmlToPlainTextWithClosingTagSpacing } from '@/lib/helpers/functions';
import { useFavoriteHandler } from '@/hooks/useFavoriteHandler';
import { STOREFRONT } from '@/lib/utils/translation';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import FavoriteLoginModal from '@/components/FavoriteLoginModal';

export interface JobPositionType {
  id: number;
  store: string;
  position: string;
  desc: string;
  salary: string;
  duration: string;
}
interface JobPositionCardProps {
  job: Job;
  storeName: string;
  city: City;
}

export const JobPositionCard = (props: JobPositionCardProps) => {
  const t = useTranslations('storefront.store_profile');
  const t_all = useTranslations();

  const { job, storeName, city } = props;
  const [showDetails, setShowDetails] = React.useState<boolean>(false);
  const [summary, setSummary] = React.useState<string>('');
  const maxLength = 40;

  // const formatDescription = (description: string) => {
  //   const cleanedDescription = description.replace(/<[^>]*>/g, ' ');
  //   return cleanedDescription.length > maxLength
  //     ? cleanedDescription.slice(0, maxLength) + '...'
  //     : cleanedDescription;
  // };

  const {
    isFavorite,
    toggleFavorite,
    isFavoriteToggleLoading,
    showLoginModal,
    setShowLoginModal,
    redirectToLogin,
  } = useFavoriteHandler('job', job.id);

  React.useEffect(() => {
    if (job) {
      if (job.description.length > 0) {
        const shortDesc = htmlToPlainTextWithClosingTagSpacing(
          job.description
        )?.slice(0, maxLength);
        setSummary(shortDesc);
      }
    }
  }, [job]);

  const closeModal = () => setShowDetails(false);
  return (
    <div
      key={job.id}
      className="job-position-card space-y-3 rounded-md bg-[rgba(24,171,118,0.20)] px-7 py-6 lg:max-w-[324px]"
    >
      <div className="flex items-center justify-between">
        <div className="truncate">
          <div className="business-name text-sm">{storeName}</div>
          <div className="position-title text-h4 font-bold">{job.title}</div>
        </div>
        <button
          onClick={toggleFavorite}
          disabled={isFavoriteToggleLoading}
          aria-label={
            isFavorite
              ? t_all(`${STOREFRONT.FAVORITES}.remove_from_favorite`)
              : t_all(`${STOREFRONT.FAVORITES}.add_to_favorite`)
          }
        >
          {isFavorite ? (
            <HeartIconSolid className="h-6 w-6 text-primary" />
          ) : (
            <HeartIcon className="h-6 w-6 text-gray-400" />
          )}
        </button>
      </div>

      <div className="job-short-summary flex items-center space-x-2 text-sm">
        <div className="">
          <p>{summary}</p>
        </div>
        <div className="" title="tooltip info">
          <TooltipNoteIcon />
        </div>
      </div>
      <div className="salary-range-job-time flex items-center space-x-5">
        {/* {job.salary != null &&
          ((typeof job.salary === 'string' && parseFloat(job.salary) > 0) ||
            (typeof job.salary === 'number' && job.salary > 0)) && (
            <div className="salary-range font-sans text-sm font-bold">
              {job.formatted_currency?.salary}/{job.salary_type_formatted}
            </div>
          )} */}
        {((typeof job.salary === 'string' && parseFloat(job.salary) > 0) ||
          (typeof job.salary === 'number' && job.salary > 0)) && (
          <span className="transform rounded-full bg-white px-4 py-2 text-sm capitalize text-paragraph transition-colors duration-500 ease-in-out hover:bg-primary hover:text-white">
            {job.formatted_currency?.salary}
            {job.salary_type != null && job.salary_type.length > 0
              ? `/${job.salary_type_formatted}`
              : ''}
          </span>
        )}
      </div>
      <div className="apply-details flex items-center justify-between pt-5">
        <a
          href={`mailto:${job.email}?subject=${job.title}`}
          className="w-auto rounded-[30px] border-2 border-[#C3E2D8] bg-white px-[25px] py-[10px] text-center text-sm text-title hover:drop-shadow-md"
        >
          {t('apply_now')}
        </a>

        <button
          className="w-auto rounded-[30px] bg-white px-[25px] py-[10px] text-sm text-title hover:text-primary"
          onClick={() => setShowDetails(true)}
        >
          {t('job_details')}
        </button>
      </div>
      {/* Details modal */}
      <HeadlessModal
        isOpen={showDetails}
        closeModal={closeModal}
        dialogBodyClass="overflow-y-auto"
      >
        <JobDetailsCard job={job} restaurantName={storeName} city={city} />
      </HeadlessModal>
      <FavoriteLoginModal
        isOpen={showLoginModal}
        setIsOpen={setShowLoginModal}
        redirectToLogin={redirectToLogin}
      />
    </div>
  );
};
